import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { reportsAPI, appointmentsAPI } from '../lib/api';
import { formatCurrency, formatTime, getTodayDate, appointmentStatusLabels } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  DollarSign, 
  Users, 
  Calendar, 
  TrendingUp,
  Package,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  Plus
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '../components/ui/skeleton';

export default function DashboardPage() {
  const { selectedBusiness } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [todayAppointments, setTodayAppointments] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      if (!selectedBusiness) return;
      
      try {
        setLoading(true);
        const [dashboardRes, appointmentsRes] = await Promise.all([
          reportsAPI.getDashboard(),
          appointmentsAPI.getAll({ date: getTodayDate() })
        ]);
        
        setStats(dashboardRes.data);
        setTodayAppointments(appointmentsRes.data.slice(0, 5));
      } catch (error) {
        logger.error('Error loading dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedBusiness]);

  const MetricCard = ({ title, value, icon: Icon, trend, trendUp, subtitle }) => (
    <Card className="metric-card" data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-3xl font-bold tracking-tight text-foreground" style={{ fontFamily: 'Manrope' }}>
              {value}
            </p>
            {subtitle && (
              <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <div className="p-3 rounded-lg bg-primary/10">
            <Icon className="w-6 h-6 text-primary" />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-4 text-sm ${trendUp ? 'text-emerald-600' : 'text-red-600'}`}>
            {trendUp ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            <span>{trend}% vs ayer</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={`skeleton-${i}`} className="h-36" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-80 lg:col-span-2" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  const chartData = stats?.weekly_sales?.map(day => ({
    name: new Date(day.date).toLocaleDateString('es-MX', { weekday: 'short' }),
    ventas: day.total,
    transacciones: day.count
  })) || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground" style={{ fontFamily: 'Manrope' }}>
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Resumen de {selectedBusiness?.name}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/agenda')}
            data-testid="quick-appointment-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Nueva cita
          </Button>
          <Button 
            onClick={() => navigate('/pos')}
            data-testid="quick-sale-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva venta
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Ventas de hoy"
          value={formatCurrency(stats?.today?.sales || 0)}
          icon={DollarSign}
          subtitle={`${stats?.today?.transactions || 0} transacciones`}
        />
        <MetricCard
          title="Citas pendientes"
          value={stats?.today?.pending_appointments || 0}
          icon={Calendar}
          subtitle="Para hoy"
        />
        <MetricCard
          title="Ticket promedio"
          value={formatCurrency(stats?.today?.average_ticket || 0)}
          icon={TrendingUp}
        />
        <MetricCard
          title="Total clientes"
          value={stats?.total_clients || 0}
          icon={Users}
        />
      </div>

      {/* Charts and Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Weekly Sales Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
              Ventas de la semana
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="name" 
                    stroke="hsl(var(--muted-foreground))" 
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))" 
                    fontSize={12}
                    tickLine={false}
                    tickFormatter={(value) => `$${value}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                    formatter={(value) => [`${formatCurrency(value)}`, 'Ventas']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="ventas" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))', strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6, fill: 'hsl(var(--primary))' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Today's Appointments */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
              Citas de hoy
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/agenda')}
              className="text-muted-foreground hover:text-foreground"
            >
              Ver todas
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {todayAppointments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No hay citas programadas</p>
              </div>
            ) : (
              todayAppointments.map((apt, index) => (
                <div 
                  key={apt.id} 
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex-shrink-0 w-12 text-center">
                    <p className="text-lg font-bold text-foreground">{formatTime(apt.start_time)}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-foreground">Cliente #{apt.client_id.slice(0, 8)}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {apt.service_ids.length} servicio(s)
                    </p>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={`status-${apt.status} text-xs`}
                  >
                    {appointmentStatusLabels[apt.status]}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Services */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
              Servicios más vendidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={stats?.top_services || []} 
                  layout="vertical"
                  margin={{ left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} />
                  <YAxis 
                    type="category" 
                    dataKey="_id" 
                    stroke="hsl(var(--muted-foreground))" 
                    fontSize={12} 
                    tickLine={false}
                    width={100}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value, name) => [
                      name === 'count' ? `${value} ventas` : formatCurrency(value),
                      name === 'count' ? 'Cantidad' : 'Ingresos'
                    ]}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Alertas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.low_stock_alerts > 0 && (
              <div 
                className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => navigate('/inventario?low_stock=true')}
              >
                <Package className="w-5 h-5 text-amber-600" />
                <div className="flex-1">
                  <p className="font-medium text-amber-800">Stock bajo</p>
                  <p className="text-sm text-amber-600">
                    {stats.low_stock_alerts} producto(s) con stock bajo
                  </p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-amber-500" />
              </div>
            )}
            
            {stats?.today?.pending_appointments > 5 && (
              <div 
                className="flex items-center gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => navigate('/agenda')}
              >
                <Clock className="w-5 h-5 text-blue-600" />
                <div className="flex-1">
                  <p className="font-medium text-blue-800">Agenda ocupada</p>
                  <p className="text-sm text-blue-600">
                    {stats.today.pending_appointments} citas pendientes para hoy
                  </p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-blue-500" />
              </div>
            )}

            {stats?.low_stock_alerts === 0 && stats?.today?.pending_appointments <= 5 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No hay alertas pendientes</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
